import { config } from '../config';
import { APIResponse, fetchAPI, headers, SpanTag, stleaf } from '../helpers';

/**
 * Processing v3 — containerised GPU Metashape runner
 * (docs/processing/trd-processing-v3.md). GA: any authenticated user, billed
 * per the requester's flavour exactly like v1 (BIR-186) — PAYG jobs with a
 * real charge return a Stripe checkout `redirectUrl` instead of a job body.
 */

/**
 * Workflow names resolved by runner v2's registry. TS source of truth,
 * mirrored by the Python `@workflow(...)` registry keys in
 * app/api/services/processing-v2/workflows/ and the API-side copy in
 * @birdi/slonik-2 (processing-workflow-run) — keep the three in lockstep.
 */
export enum ProcessingWorkflow {
  Default = 'default',
}

export type ProcessingV3Source =
  | { type: 'folder'; id: string }
  | { type: 'folders'; ids: string[] };

/**
 * GeoJSON Polygon in WGS84 lon/lat — `coordinates` is an array of linear
 * rings, `coordinates[0]` the outer ring (a closed ring of ≥4 `[lon, lat]`
 * positions). Matches the API's `boundary` zod schema in router-v3.ts.
 */
export interface GeoJSONPolygon {
  type: 'Polygon';
  coordinates: number[][][];
}

/**
 * GeoJSON MultiPolygon in WGS84 lon/lat. `coordinates` is an array of
 * polygons, each an array of linear rings. Every polygon's outer ring must be
 * a closed ring of ≥4 `[lon, lat]` positions. The runner clips to the outer
 * ring of every polygon, so all of them survive the trip (BIR-316).
 */
export interface GeoJSONMultiPolygon {
  type: 'MultiPolygon';
  coordinates: number[][][][];
}

/**
 * Per-output processing settings (BIR-316): the resolution, point-cloud,
 * mesh and RTK choices the map form collects. Sparse on purpose: omit a key
 * and the runner uses its default, so the client never has to invent one.
 * Embedded in the job manifest verbatim.
 */
export interface ProcessingV3Settings {
  /** Orthomosaic resolution in METRES per pixel. 0 = native GSD. */
  orthoResolution?: number;
  /** DSM resolution in METRES per pixel. 0 = native GSD. */
  dsmResolution?: number;
  /** DTM resolution in METRES per pixel. 0 = native GSD. */
  dtmResolution?: number;
  /** Build a point cloud output. Absent = true. */
  pointCloud?: boolean;
  /** Build a textured mesh output. Absent = true. */
  texturedMesh?: boolean;
  /** Treat the camera positions as RTK-accurate. Absent = false. */
  useRTK?: boolean;
}

export interface ProcessingV3Request {
  source: ProcessingV3Source;
  target: { type: 'map'; id: string };
  workflow: ProcessingWorkflow;
  name?: string;
  /**
   * Restrict the source set to nadir-pitch images (85°–95°) — the form's
   * nadir toggle. The filtered set is what gets billed, so the quoted
   * estimate (which the form fetches with the same flag) and the charge
   * cover the same images (v1 parity, BIR-186).
   */
  nadirOnly?: boolean;
  /**
   * Output CRS as a spatial_ref_sys srid (the `srid` of a catalogue `CRS`
   * object, i.e. what the form's CRS selector holds). Optional — omitted means
   * the runner auto-derives a WGS84/UTM zone from the camera geotags.
   */
  crs?: { srid: number };
  /**
   * Area-of-interest clip boundary as a GeoJSON Polygon or MultiPolygon in
   * WGS84 lon/lat. Passed to the manifest verbatim; the runner clips to the
   * outer ring of every polygon. Optional, and omitting it means no clip.
   */
  boundary?: GeoJSONPolygon | GeoJSONMultiPolygon;
  /**
   * Per-output settings from the form (resolutions in metres, point cloud,
   * textured mesh, RTK). Optional, and omitting it means the runner
   * defaults.
   */
  settings?: ProcessingV3Settings;
}

export interface ProcessingWorkflowRun {
  id: string;
  processingJobId: string;
  workflow: string;
  state: 'queued' | 'running' | 'succeeded' | 'failed';
  manifestS3Key: string;
  outputS3Key: string | null;
  sfnExecutionArn: string | null;
  error: unknown;
  startedAt: string | null;
  endedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProcessingV3JobResponse {
  id: string;
  name: string;
  mapId: string;
  mediaSetId: string;
  workflowRun: ProcessingWorkflowRun;
}

/**
 * PAYG jobs whose cost is not fully credit-covered pay BEFORE the job runs:
 * the API responds with the Stripe checkout URL instead of a job body, and
 * checkout success dispatches the job (same convention as v1's
 * postPaygJobV2).
 */
export interface ProcessingV3CheckoutRedirect {
  redirectUrl: string;
}

export const postProcessingJobV3 = (
  st: SpanTag,
  requestObj: ProcessingV3Request,
): Promise<
  APIResponse<ProcessingV3JobResponse | ProcessingV3CheckoutRedirect>
> => {
  const url = `${config.API_URL}/v3/processing-jobs`;
  return fetchAPI(stleaf(st, 'pjv3po'), url, {
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify(requestObj),
    headers,
  });
};

export const getProcessingJobV3Runs = (
  st: SpanTag,
  processingJobId: string,
): Promise<APIResponse<ProcessingWorkflowRun[]>> => {
  const url = `${config.API_URL}/v3/processing-jobs/${processingJobId}/runs`;
  return fetchAPI(stleaf(st, 'pjv3rn'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};
